import React, { FC, useEffect, useRef } from 'react';

import * as Blockly from 'blockly';
import { WorkspaceSvg } from 'react-blockly';
import XMLParser from 'react-xml-parser';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { javascriptGenerator } from 'blockly/javascript';

import { toast } from 'react-toastify';

import { CompositeMetricData } from '@app/interfaces/metric.type';
import useTranslation from '@app/hooks/use-translation';

import { buildingJsonFromMetricFormula } from './data/json-rebuild.data';
import { useToolboxCategories } from './data/toolbox-categories.data';

type BlocklyNativeProps = {
  initialXml: string;
  onChange: ({ formula }: { formula: CompositeMetricData['formula'] }) => void;
  onCode: (code: string) => void;
};

const BlocklyNative: FC<BlocklyNativeProps> = ({ initialXml, onChange, onCode }) => {
  const BLOCK_ELEMENT = 'blocklyDiv';

  const { toolboxCategories } = useToolboxCategories();
  const { t } = useTranslation('pages.metric');

  const workspaceRef = useRef<WorkspaceSvg | null>(null);

  /**
   * XML, который последний раз был применён
   * именно извне.
   */
  const appliedInitialXmlRef = useRef('');

  /**
   * Флаг:
   *
   * "изменение initialXml сейчас является следствием
   * изменения, которое только что сделал Blockly".
   *
   * В таком случае workspace НЕ перезагружаем.
   */
  const skipNextExternalUpdateRef = useRef(false);

  const onChangeRef = useRef(onChange);
  const onCodeRef = useRef(onCode);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  /**
   * Получаем JSON/formula из текущего Blockly workspace.
   */
  const getFormulaFromWorkspace = (
    workspace: WorkspaceSvg,
  ): CompositeMetricData['formula'] | null => {
    try {
      const xmlWorkspace = Blockly.Xml.workspaceToDom(workspace);

      if (!xmlWorkspace) {
        return null;
      }

      const xmlString = new XMLSerializer().serializeToString(xmlWorkspace);

      const parsedXml = new XMLParser().parseFromString(xmlString, 'text/xml');

      const firstChild = parsedXml?.children?.[0];

      if (!firstChild) {
        return null;
      }

      return buildingJsonFromMetricFormula(firstChild, 'formula');
    } catch (error) {
      console.error('Failed to build formula from Blockly:', error);

      return null;
    }
  };

  /**
   * Генерация JavaScript из текущего workspace.
   */
  const updateJavaScriptCode = (workspace: WorkspaceSvg) => {
    try {
      const code = javascriptGenerator.workspaceToCode(workspace);

      onCodeRef.current(code);
    } catch (error) {
      console.error('Failed to generate JavaScript code:', error);
    }
  };

  /**
   * Blockly -> React.
   */
  useEffect(() => {
    const handleBlocklyChange = (event: Blockly.Events.Abstract) => {
      const workspace = workspaceRef.current;

      if (!workspace) {
        return;
      }

      const supportedEvents = [
        'create',
        'move',
        'change',
        'delete',
        'block_field_intermediate_change',
      ];

      if (!supportedEvents.includes(event.type)) {
        return;
      }

      try {
        /**
         * Очень важно:
         *
         * Следующее изменение initialXml,
         * которое произойдёт из-за этого события,
         * НЕ должно приводить к workspace.clear().
         */
        skipNextExternalUpdateRef.current = true;

        /**
         * Обновляем formula.
         */
        const formula = getFormulaFromWorkspace(workspace);

        if (formula) {
          onChangeRef.current({
            formula,
          });
        }

        /**
         * И СРАЗУ обновляем JavaScript.
         *
         * Не ждём React render.
         */
        updateJavaScriptCode(workspace);
      } catch (error) {
        console.error('Blockly change handler error:', error);

        skipNextExternalUpdateRef.current = false;
      }
    };

    /**
     * Инициализация Blockly.
     */
    if (!workspaceRef.current) {
      try {
        const workspace = Blockly.inject(BLOCK_ELEMENT, {
          toolbox: toolboxCategories,
          sounds: false,

          zoom: {
            controls: true,
            wheel: true,
            startScale: 1,
            maxScale: 3,
            minScale: 0.3,
            scaleSpeed: 1.2,
            pinch: true,
          },
        });

        workspaceRef.current = workspace;

        /**
         * Загружаем initialXml.
         *
         * События отключены, потому что это
         * не пользовательское изменение.
         */
        Blockly.Events.disable();

        try {
          const parser = new window.DOMParser();

          const doc = parser.parseFromString(initialXml, 'text/xml').documentElement;

          Blockly.Xml.appendDomToWorkspace(doc, workspace);

          appliedInitialXmlRef.current = initialXml;
        } finally {
          Blockly.Events.enable();
        }

        /**
         * После загрузки ставим listener.
         */
        workspace.addChangeListener(handleBlocklyChange);

        /**
         * Первый JavaScript code.
         */
        updateJavaScriptCode(workspace);
      } catch (error) {
        console.error('Blockly initialization error:', error);

        toast.error(t('error_metric_build'));
      }
    }

    return () => {
      const workspace = workspaceRef.current;

      if (!workspace) {
        return;
      }

      try {
        workspace.removeChangeListener(handleBlocklyChange);
      } catch (error) {
        console.error('Failed to remove Blockly listener:', error);
      }

      try {
        workspace.dispose();
      } catch (error) {
        console.error('Failed to dispose Blockly workspace:', error);
      }

      workspaceRef.current = null;
    };

    // Blockly workspace создаём только один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * React -> Blockly.
   *
   * Этот effect отвечает ТОЛЬКО за внешние изменения.
   *
   * Например:
   *
   * выбрать другой шаблон
   * открыть другую метрику
   * загрузить сохранённую формулу
   */
  useEffect(() => {
    const workspace = workspaceRef.current;

    if (!workspace) {
      return;
    }

    /**
     * XML вообще не изменился.
     */
    if (appliedInitialXmlRef.current === initialXml) {
      return;
    }

    /**
     * Это изменение пришло от Blockly.
     *
     * НЕ делаем clear().
     *
     * Именно это предотвращает исчезновение
     * блоков при добавлении нового блока.
     */
    if (skipNextExternalUpdateRef.current) {
      skipNextExternalUpdateRef.current = false;

      appliedInitialXmlRef.current = initialXml;

      return;
    }

    /**
     * Здесь действительно пришёл новый XML
     * извне.
     */
    try {
      const parser = new window.DOMParser();

      const doc = parser.parseFromString(initialXml, 'text/xml').documentElement;

      Blockly.Events.disable();

      try {
        /**
         * Только здесь разрешаем clear().
         */
        workspace.clear();

        Blockly.Xml.appendDomToWorkspace(doc, workspace);

        appliedInitialXmlRef.current = initialXml;
      } finally {
        Blockly.Events.enable();
      }

      /**
       * После внешней загрузки тоже пересчитываем JS.
       */
      updateJavaScriptCode(workspace);
    } catch (error) {
      console.error('Blockly external XML update error:', error);

      skipNextExternalUpdateRef.current = false;

      toast.error(t('error_metric_build'));
    }
  }, [initialXml, t]);

  return <div id={BLOCK_ELEMENT} className="h-[100vh] w-[100%] z-50" />;
};

export default BlocklyNative;
